// Hired-ship upkeep (design doc 16.54, LOCKED interview pass): "a real
// ongoing credit charge: 5% of the ship's Shipyard cost, per real-clock-
// hour," replacing the old one-time 25%-of-Shipyard-cost hire fee. Owned-
// ship upkeep is unaffected (still food/fuel only, Section 10) -- this
// only touches "hired" fleet entries.
import { effectiveNow } from "./gameClock";
import type { AppState, FleetShip } from "./ui/state";

// 16.92 divided this by 100 alongside the 100x hull reprice, holding the
// absolute hourly cost of a hired escort exactly where it was. Upkeep is a
// fraction of costCr, so leaving it alone would have turned a modest hourly fee
// into one that outruns any income in the game.
export const HIRED_UPKEEP_RATE_PER_HOUR = 0.0005;
const MS_PER_HOUR = 60 * 60 * 1000;

export function hourlyUpkeepCr(ship: FleetShip["ship"]): number {
  return ship.costCr * HIRED_UPKEEP_RATE_PER_HOUR;
}

// Recomputed fresh whenever called (same "derive, don't cache" convention
// as missionChecks.ts's runOpportunisticChecks) -- bills every hired ship
// for whatever real-clock time has elapsed since it was last billed
// (`lastUpkeepAt`, advanced only on an actual successful/partial bill,
// never on hire alone). A ship whose captain can't cover what's owed is
// repossessed for nonpayment -- the same "crew acts in its own interest
// rather than go unpaid" philosophy Section 11 already applies to
// starving owned crew, just extended to a hired ship's own crew/owner.
// Returns true if anything changed (a bill was paid or a ship
// repossessed), so callers know whether a save is warranted.
export function runHiredShipUpkeep(state: AppState, nowMs: number): boolean {
  const now = effectiveNow(state.clock, nowMs);
  let changed = false;
  const remaining: FleetShip[] = [];

  for (const f of state.fleet) {
    if (f.source !== "hired" || f.lastUpkeepAt == null) {
      remaining.push(f);
      continue;
    }
    const elapsedHours = (now - f.lastUpkeepAt) / MS_PER_HOUR;
    if (elapsedHours <= 0) {
      remaining.push(f);
      continue;
    }
    const owed = Math.round(hourlyUpkeepCr(f.ship) * elapsedHours);
    if (owed <= 0) {
      remaining.push(f);
      continue;
    }
    changed = true;
    if (state.credits >= owed) {
      state.credits -= owed;
      f.lastUpkeepAt = now;
      remaining.push(f);
      continue;
    }
    // Can't cover what's owed -- repossessed. Pays out whatever credits
    // remain first (a partial payment, not a free pass), same as a
    // starving crew still eating whatever Provisions are left before
    // walking.
    state.credits = 0;
    state.lastEvent = `${f.ship.name} (hired) was repossessed -- rental upkeep went unpaid.`;
    if (state.currentShipInstanceId === f.id) {
      state.currentShip = null;
      state.currentShipInstanceId = null;
    }
  }

  state.fleet = remaining;
  return changed;
}
